/**
 * The expanded quote: one block per leg (the stops on a rail, the carrier, the
 * travel time), with the pickup and delivery trucking before and after the
 * quote's own legs. The quote's price and facts show once, beside its last
 * leg: the price is the whole trip's. Each leg of a multi-leg quote shows its
 * own CO₂ when the estimate has it. A trucking leg has a price of its own.
 */
import React from 'react';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';

import type {
  AccessLegs,
  LaneMode,
  NormalizedQuote,
  TruckingLeg,
} from '../../../apis/lanePlanner';
import { formatKgCo2e } from '@/utils/formatEmissions';
import { formatLegTime } from './localTime';
import CarrierChip from './CarrierChip';
import {
  formatBusinessDays,
  formatMoney,
  formatTransit,
  emissionsDisclosureOf,
  legCo2Of,
  legLogoOf,
  legTravelLabelOf,
  layoverMinutesOf,
} from './quoteView';
import styles from './styles.module.css';
import {
  arrivalOf,
  pickupDayOf,
  truckingCaveatText,
  TruckingChoices,
  TruckingLegView,
  truckingHintsOf,
  arrivalDayOf,
  truckingLegsOf,
  truckingTotalOf,
} from './truckingView';

const Fact: React.FC<{
  label: string;
  value: string | null;
  tooltip?: string;
}> = ({ label, value, tooltip }) => {
  if (!value) return null;
  const figure = (
    <span className={styles.legFactValue} tabIndex={tooltip ? 0 : undefined}>
      {value}
    </span>
  );
  return (
    <div className={styles.legFact}>
      <span>{label}:</span>
      {tooltip ? (
        <Tooltip title={tooltip} trigger={['hover', 'focus']}>
          {figure}
        </Tooltip>
      ) : (
        figure
      )}
    </div>
  );
};

/** The stop's gap before leg `index`; "Layover time unknown" when its stamps don't resolve, nothing for a zero gap. */
const layoverTextOf = (
  quote: NormalizedQuote,
  index: number
): string | null => {
  if (index === 0) return null;
  const minutes = layoverMinutesOf(quote, index);
  if (minutes == null) return 'Layover time unknown';
  const text = formatTransit(minutes, 'long');
  return text && `${text} layover`;
};

const Stop: React.FC<{ place: string | null; time: string | null }> = ({
  place,
  time,
}) => (
  <div>
    <div className={styles.legStopName}>{place ?? '—'}</div>
    {time && <div className={styles.legStopTime}>{time}</div>}
  </div>
);

const QuoteFigures: React.FC<{
  quote: NormalizedQuote;
  vendorName: string;
  /** The CO₂ of the leg beside it, when the quote's is split by leg. */
  legCo2?: number | null;
}> = ({ quote, vendorName, legCo2 }) => (
  <div className={styles.legSide}>
    {quote.charges.length > 0 ? (
      <div className={styles.chargeList}>
        {quote.charges.map((charge, index) => (
          // charges have no id of their own and the list never reorders
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${charge.label}-${index}`} className={styles.detailRow}>
            <span>{charge.label}</span>
            <span>{formatMoney(charge.amount, quote.currency)}</span>
          </div>
        ))}
        <div className={`${styles.detailRow} ${styles.detailRowTotal}`}>
          <span>Total</span>
          <span>{formatMoney(quote.price, quote.currency)}</span>
        </div>
      </div>
    ) : (
      quote.price != null && (
        <div className={styles.legPrice}>
          {formatMoney(quote.price, quote.currency)}
        </div>
      )
    )}
    {legCo2 !== undefined && (
      <Fact
        label='CO₂ estimate'
        value={formatKgCo2e(legCo2)}
        tooltip={emissionsDisclosureOf(quote)}
      />
    )}
    <Fact
      label={legCo2 !== undefined ? 'Trip CO₂ estimate' : 'CO₂ estimate'}
      value={formatKgCo2e(quote.co2_kg)}
      tooltip={emissionsDisclosureOf(quote)}
    />
    <Fact
      label='Quote valid until'
      value={
        quote.valid_until ? dayjs(quote.valid_until).format('M/D/YY') : null
      }
    />
    <Fact label='Capacity' value={quote.capacity} />
    <Fact label='Cold chain' value={quote.cold_chain} />
    <Fact label='Temperature' value={quote.temperature_range} />
    <Fact label='Monitoring' value={quote.monitoring} />
    <Fact label='Ref' value={quote.vendor_quote_id} />
    <Fact
      label='Carrier quote no.'
      value={
        quote.mode === 'road' && typeof quote.details?.quote_number === 'string'
          ? quote.details.quote_number
          : null
      }
    />
    <Fact label='Via' value={vendorName} />
  </div>
);

interface LegBlockProps {
  mode: LaneMode;
  logoUrl?: string | null;
  carrierName?: string | null;
  from: { place: string | null; time: string | null };
  to: { place: string | null; time: string | null };
  travel: string | null;
  /** The carrier line, or why the leg has none. */
  carrier: React.ReactNode;
  side?: React.ReactNode;
}

const LegBlock: React.FC<LegBlockProps> = ({
  mode,
  logoUrl,
  carrierName,
  from,
  to,
  travel,
  carrier,
  side,
}) => (
  <div className={styles.legBlock}>
    <span className={styles.legRail} aria-hidden />
    <span className={`${styles.legDot} ${styles.legDotStart}`} />
    <div className={styles.legStopStart}>
      <Stop {...from} />
    </div>
    <CarrierChip
      className={styles.legChip}
      mode={mode}
      logoUrl={logoUrl}
      carrier={carrierName}
    />
    {travel && <div className={styles.legTravel}>Travel time: {travel}</div>}
    <div className={styles.legCarrier}>{carrier}</div>
    <span className={`${styles.legDot} ${styles.legDotEnd}`} />
    <div className={styles.legStopEnd}>
      <Stop {...to} />
    </div>
    {side}
  </div>
);

const TruckingLegBlock: React.FC<{
  view: TruckingLegView;
  shipDate?: string;
  /** The day the quote arrives (YYYY-MM-DD), when known: a delivery truck leaves on it. */
  arrivalDay?: string | null;
  onChange?: (leg: TruckingLeg, site: string) => void;
}> = ({ view, shipDate, arrivalDay, onChange }) => {
  const pickup = pickupDayOf(view.leg, shipDate, arrivalDay);
  const arrival = arrivalOf(pickup, view.transitMinutes);
  return (
    <>
      <LegBlock
        mode='road'
        logoUrl={view.logoUrl}
        carrierName={view.carrier}
        from={{
          place: view.from,
          time: pickup ? `Pickup ${pickup.format('ddd, M/D/YY')}` : null,
        }}
        to={{
          place: view.to,
          time: arrival ? `Arrives ~${arrival.format('M/D/YY')}` : null,
        }}
        travel={formatBusinessDays(view.transitMinutes)}
        carrier={
          view.reason ?? (
            <>
              <strong>{view.carrier ?? 'Carrier'}</strong>
              {view.serviceLevel && ` · ${view.serviceLevel}`}{' '}
              {onChange && view.canChange && (
                <button
                  type='button'
                  className={styles.legChange}
                  aria-label={`Change ${view.leg} trucking carrier`}
                  onClick={() => onChange(view.leg, view.site)}
                >
                  change
                </button>
              )}
            </>
          )
        }
        side={
          view.price && (
            <div className={styles.legSide}>
              <div className={styles.chargeList}>
                <div className={`${styles.detailRow} ${styles.legPriceRow}`}>
                  <span>Estimated</span>
                  <span>{view.price}</span>
                </div>
                {view.note && <div className={styles.legSub}>{view.note}</div>}
              </div>
              <Fact
                label='CO₂ estimate'
                value={view.co2}
                tooltip={view.co2Disclosure ?? undefined}
              />
            </div>
          )
        }
      />
    </>
  );
};

const QuoteDetails: React.FC<{
  quote: NormalizedQuote;
  vendorName: string;
  shipDate?: string;
  accessLegs?: AccessLegs;
  choices?: TruckingChoices;
  onChangeTrucking?: (leg: TruckingLeg, site: string) => void;
}> = ({
  quote,
  vendorName,
  shipDate,
  accessLegs,
  choices,
  onChangeTrucking,
}) => {
  const legsCo2 = legCo2Of(quote);
  const lastLeg = quote.legs.length - 1;
  const sideOf = (index: number): React.ReactNode => {
    if (index === lastLeg)
      return (
        <QuoteFigures
          quote={quote}
          vendorName={vendorName}
          legCo2={legsCo2 ? legsCo2[index] : undefined}
        />
      );
    return (
      legsCo2 && (
        <div className={styles.legSide}>
          <Fact
            label='CO₂ estimate'
            value={formatKgCo2e(legsCo2[index])}
            tooltip={emissionsDisclosureOf(quote)}
          />
        </div>
      )
    );
  };
  const trucking = truckingLegsOf(quote, accessLegs, choices, shipDate);
  const pickup = trucking.find((view) => view.leg === 'pickup');
  const delivery = trucking.find((view) => view.leg === 'delivery');
  const hints = truckingHintsOf(quote, accessLegs);
  const asked = trucking.length > 0 || hints.length > 0;
  const total = asked
    ? truckingTotalOf(quote, accessLegs, choices, shipDate)
    : null;
  const caveat = asked ? truckingCaveatText(accessLegs) : null;
  const truckingBlock = (view: TruckingLegView): React.ReactNode => (
    <TruckingLegBlock
      view={view}
      shipDate={shipDate}
      arrivalDay={arrivalDayOf(quote)}
      onChange={onChangeTrucking}
    />
  );
  return (
    <div className={styles.legList}>
      {pickup && (
        <>
          {truckingBlock(pickup)}
          <div className={styles.layover} aria-hidden />
        </>
      )}
      {quote.legs.length === 0 ? (
        <div className={`${styles.legBlock} ${styles.legBlockEmpty}`}>
          <div className={`${styles.muted} ${styles.legEmpty}`}>
            Schedule not provided by the vendor.
          </div>
          <QuoteFigures quote={quote} vendorName={vendorName} />
        </div>
      ) : (
        quote.legs.map((leg, index) => {
          const layover = layoverTextOf(quote, index);
          return (
            // legs have no id of their own and the list never reorders
            // eslint-disable-next-line react/no-array-index-key
            <React.Fragment key={`${leg.service_ref ?? ''}-${index}`}>
              {layover && (
                <div className={styles.layover}>
                  <span>
                    {layover}
                    {leg.origin ? `, ${leg.origin}` : ''}
                  </span>
                </div>
              )}
              <LegBlock
                mode={leg.mode}
                logoUrl={legLogoOf(leg)}
                carrierName={leg.carrier}
                from={{
                  place: leg.origin,
                  time: formatLegTime(quote, index, 'departs'),
                }}
                to={{
                  place: leg.destination,
                  time: formatLegTime(quote, index, 'arrives'),
                }}
                travel={legTravelLabelOf(quote, index)}
                carrier={
                  <>
                    {leg.carrier && <strong>{leg.carrier}</strong>}
                    {leg.carrier && leg.service_ref && ' · '}
                    {leg.service_ref}
                  </>
                }
                side={sideOf(index)}
              />
            </React.Fragment>
          );
        })
      )}
      {delivery && (
        <>
          <div className={styles.layover} aria-hidden />
          {truckingBlock(delivery)}
        </>
      )}
      {(total || caveat || hints.length > 0) && (
        <div className={styles.legFooter}>
          {hints.map((hint) => (
            <div key={hint} className={styles.muted}>
              {hint}
            </div>
          ))}
          {total && (
            <div className={`${styles.detailRow} ${styles.detailRowTotal}`}>
              <span>
                {total.label}
                {total.note ? ` (${total.note})` : ''}
              </span>
              <span>{total.value}</span>
            </div>
          )}
          {total?.co2 && (
            <div className={styles.legFooterFacts}>
              <div className={styles.legFooterFigures}>
                <Fact
                  label={`Door-to-door CO₂ estimate${total.co2Note ? ` (${total.co2Note})` : ''}`}
                  value={total.co2}
                  tooltip={total.co2Disclosure ?? undefined}
                />
              </div>
            </div>
          )}
          {caveat && <div className={styles.muted}>{caveat}</div>}
        </div>
      )}
    </div>
  );
};

export default QuoteDetails;
